import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import ProductQuantitySelector from '../product-quantity-selector/ProductQuantitySelector'
import { IOrderProductDeclineForm } from '../../data-handlers/order/IOrderDataHandler'
import { useGlobalLoading } from '../../contexts/GlobalLoadingContext'

import RadioMui from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlMui from '@mui/material/FormControl'
import FormControlLabelMui from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import TextField from '@mui/material/TextField'
import { Button } from '@mui/material'

import { styled } from '@mui/material/styles'
import messages from './ProductDeclineForm.messages'

export interface IOwnProps {
  className?: string
  product: OrderProduct
  onSubmit?: () => void
}
export interface IStateProps {
}

export interface IDispatchProps {
  requestProductDecline: (product: OrderProduct, declineData: IOrderProductDeclineForm) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps;

const COMMENT_MAX_SYMBOL = 40

//import messages from './ProductDeclineForm.messages'

const Title = styled(FormLabel)(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(),
  color: theme.palette.blackColor.light,
  fontSize: 18,
  fontWeight: 700,
  '&.Mui-focused': {
    color: theme.palette.blackColor.light,
  },
}))

const FormButton = styled(Button)({
  textTransform: 'uppercase',
})

const FormQuantitySelector = styled(ProductQuantitySelector)(({ theme }) => ({
  marginBottom: theme.spacing(7),
}))

const FormControl = styled(FormControlMui)(({ theme }) => ({
  marginBottom: theme.spacing(),
}))

const FormField = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(4),
}))

export interface FormFieldLabelProps {
  isMaxLengthReached: boolean
}

const FormFieldLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'isMaxLengthReached',
})<FormFieldLabelProps>(({ theme, isMaxLengthReached }) => ({
  color: theme.palette.greyColor.light,
  position: 'absolute',
  top: '50%',
  right: 15,
  transform: 'translateY(-50%)',
  ...(isMaxLengthReached && {
    color: theme.palette.error.main,
  }),
}))

const FormFieldText = styled(TextField)(({ theme }) => ({
  borderRadius: 0,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    border: `1px solid ${theme.palette.greyColor.dark}`,
    fontSize: 18,
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(5),
    '&.Mui-focused fieldset': {
      borderWidth: 1,
      borderColor: theme.palette.greyColor.dark,
      boxShadow: `1px 0 0 0 ${theme.palette.greyColor.dark}`,
    },
  },
}))

const FormControlLabel = styled(FormControlLabelMui)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  '& + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiFormControlLabel-label': {
    padding: '0 5px',
  },
}))

const Radio = styled(RadioMui)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  padding: 0,
  '&.Mui-checked': {
    color: theme.palette.text.secondary,
  },
}))

const ProductDeclineForm: React.FC<IProps> = (props) => {
  const {
    product,
    requestProductDecline,
    className,
    onSubmit,
  } = props

  const { showLoading, hideLoading } = useGlobalLoading()

  const [quantityPossibleToDeliver, setQuantityPossibleToDeliver] = React.useState(product.quantity)
  const [reasonText, setReasonText] = React.useState('')
  const [reasonCode, setReasonCode] = React.useState('')

  const handleQuantityChange = (quantity: number) => {
    setQuantityPossibleToDeliver(quantity)
  }

  const handleReasonTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonText(event.target.value)
  }

  const handleDeclineOrder = () => {
    showLoading()
    setTimeout(() => hideLoading(), 2000)

    if (quantityPossibleToDeliver < product.quantity) {
      requestProductDecline(product, {
        quantity: quantityPossibleToDeliver,
        reasonCode: reasonCode,
        reasonText: reasonText,
      })
    }

    if (onSubmit) {
      onSubmit()
    }
  }

  const handleReasonCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReasonCode(event.target.value)
  }

  const intl = useIntl()

  return (
    <div className={className}>
      <FormQuantitySelector
        maximum={product.quantity}
        productAmount={quantityPossibleToDeliver}
        onChange={handleQuantityChange}
      />
      {
        quantityPossibleToDeliver < product.quantity
        &&
        <>
          <FormControl>
            <Title id='demo-reason-code-label'>
              <FormattedMessage {...messages.title} />
            </Title>
            <RadioGroup
              aria-labelledby='demo-reason-code-label'
              defaultValue='other'
              name='reason-code'
              onChange={handleReasonCodeChange}
            >
              <FormControlLabel value='Out of stock' control={<Radio />} label='Out of stock' />
              <FormControlLabel value='My mistake' control={<Radio />} label='My mistake' />
              <FormControlLabel value='other' control={<Radio />} label='Other' />
            </RadioGroup>
          </FormControl>
          <FormField>
            <FormFieldText
              id='outlined-multiline-static'
              InputProps={{
                inputProps: {
                  maxLength: COMMENT_MAX_SYMBOL,
                },
              }}
              label={null}
              multiline
              rows={3}
              defaultValue={reasonText}
              onChange={handleReasonTextChange}
            />
            <FormFieldLabel isMaxLengthReached={reasonText.length >= COMMENT_MAX_SYMBOL}>{COMMENT_MAX_SYMBOL - reasonText.length}</FormFieldLabel>
          </FormField>
        </>
      }
      <FormButton
        onClick={handleDeclineOrder}
        variant='contained'
        color='primary'
        disableElevation
        aria-label={intl.formatMessage({ ...messages.formButton })}
      >
        <FormattedMessage {...messages.formButton} />
      </FormButton>
    </div>
  )
}

export default styled(ProductDeclineForm)({
  display: 'flex',
  flexDirection: 'column',
})
